// /pricing — honest pricing page for the beta era.
//
// Positioning (from the July 2026 competitive assessment): the market's
// loudest emotion is paywall rage (daily caps, forced ads, $50/yr for pizza
// math). So we commit in writing to the two things users fear losing:
//   1. Manual splitting stays free forever.
//   2. When we do charge, it leads with pay-PER-TRIP — not a subscription.
// Nothing here promises features that don't exist: today everything is free
// and clearly labeled beta; the paid tier is shown as "planned".
import { Link } from 'react-router-dom'
import { Check, Sparkles, FileText, Users } from 'lucide-react'
import PublicShell from '../components/PublicShell'

const FREE_FOREVER = [
  'Unlimited manual expenses — no daily caps, ever',
  'Unlimited group members',
  'Equal, percentage, and exact splits',
  'Settle-up math that minimizes transfers',
  'Share links — friends see what they owe without signing up',
  'No ads, no countdown timers',
]

const MAGIC = [
  'Upload a whole PDF/CSV statement — every charge extracted',
  'Snap a bank-app screenshot — same magic, no file needed',
  'AI receipt scanning with per-item splits',
  'Trip mode: everyday charges outside your dates auto-excluded',
  'Household mode: shared-vs-personal triage that learns your merchants',
  'Bulk edit: assign, categorize, confirm dozens of charges at once',
]

export default function PricingPage() {
  return (
    <PublicShell
      title="AutoSplit Pricing — Free While in Beta"
      description="AutoSplit is free while in beta. Manual splitting stays free forever — no daily caps, no ads. Planned pricing: pay per trip, not another subscription."
    >
      <main className="relative z-10 max-w-5xl mx-auto px-6 pt-8 pb-16">

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-lime-400/10 border border-lime-400/30 text-lime-400 text-xs font-semibold mb-5">
            <Sparkles size={12} />
            Everything is free while we're in beta
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mb-4">
            Pricing without the rage
          </h1>
          <p className="text-ink-300 text-lg leading-relaxed">
            You've seen the other apps: daily expense caps, unskippable ads,
            $50-a-year subscriptions to split a pizza. We're building the
            opposite — and putting it in writing.
          </p>
        </div>

        {/* ── The two tiers ─────────────────────────────────────────────── */}
        <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">

          {/* Free forever */}
          <div className="rounded-2xl border border-ink-700 bg-ink-900/70 p-7">
            <div className="flex items-center gap-2 mb-1">
              <Users size={16} className="text-ink-300" />
              <h2 className="font-display text-xl font-semibold">Splitting</h2>
            </div>
            <div className="font-mono text-3xl font-bold mb-1">$0</div>
            <p className="text-xs text-ink-400 mb-5">Free forever. Not "free tier" free — actually free.</p>
            <ul className="space-y-2.5">
              {FREE_FOREVER.map(f => (
                <li key={f} className="flex items-start gap-2 text-sm text-ink-200">
                  <Check size={14} className="text-lime-400 mt-0.5 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* The statement magic — free in beta, per-trip later */}
          <div className="rounded-2xl border border-lime-400/30 bg-lime-400/5 p-7 relative overflow-hidden">
            <div className="absolute top-4 right-4 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-lime-400/15 text-lime-300 border border-lime-400/30">
              Free in beta
            </div>
            <div className="flex items-center gap-2 mb-1">
              <FileText size={16} className="text-lime-400" />
              <h2 className="font-display text-xl font-semibold">Statement magic</h2>
            </div>
            <div className="font-mono text-3xl font-bold mb-1">
              $0 <span className="text-base text-ink-400 font-normal">now</span>
            </div>
            <p className="text-xs text-ink-400 mb-5">
              Planned: <span className="text-ink-200 font-medium">$4.99 per trip</span> — pay when you travel,
              not every month. No subscription required, ever.
            </p>
            <ul className="space-y-2.5">
              {MAGIC.map(f => (
                <li key={f} className="flex items-start gap-2 text-sm text-ink-200">
                  <Check size={14} className="text-lime-400 mt-0.5 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ── The promises ─────────────────────────────────────────────── */}
        <div className="max-w-3xl mx-auto mt-10 rounded-2xl border border-ink-700 bg-ink-900/50 p-7">
          <h3 className="font-display text-lg font-semibold mb-4">Three promises, in writing</h3>
          <ol className="space-y-3 text-sm text-ink-300 list-decimal list-inside">
            <li>
              <span className="text-ink-100 font-medium">Manual splitting stays free forever.</span>{' '}
              No daily caps. No ads. If you're happy typing expenses in, you'll never pay us a cent.
            </li>
            <li>
              <span className="text-ink-100 font-medium">When we charge, it's per trip — not a subscription first.</span>{' '}
              Trips are occasional; your bill should be too.
            </li>
            <li>
              <span className="text-ink-100 font-medium">Beta users will get notice before anything changes.</span>{' '}
              No surprise paywalls mid-trip. We watched that movie; we hated it too.
            </li>
          </ol>
        </div>

        {/* ── CTA ──────────────────────────────────────────────────────── */}
        <div className="text-center mt-12">
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-lime-400 text-ink-950 font-bold hover:bg-lime-500 transition-colors shadow-lg shadow-lime-400/20"
          >
            Start free — it's all free right now
          </Link>
          <p className="text-xs text-ink-500 mt-3">No credit card. No expense caps. No countdown timers.</p>
        </div>
      </main>
    </PublicShell>
  )
}
